import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useVerifyOTP, useResendOTP } from '@/hooks/use-auth';
import { RefreshCwIcon, CheckIcon } from 'lucide-react';

interface OTPVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  onVerificationSuccess: (userData?: any) => void;
}

export default function OTPVerificationModal({
  isOpen,
  onClose,
  email,
  onVerificationSuccess,
}: OTPVerificationModalProps) {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(30);
  const [canResend, setCanResend] = useState(false);

  const { toast } = useToast();
  const { handleError } = useErrorHandler();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // API mutations
  const verifyOTPMutation = useVerifyOTP();
  const resendOTPMutation = useResendOTP();

  // Timer for resend functionality
  useEffect(() => {
    if (isOpen && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setCanResend(true);
    }
  }, [isOpen, timeLeft]);

  // Reset timer when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeLeft(30);
      setCanResend(false);
      setOtp(['', '', '', '', '', '']);
      // Focus first input
      const focusTimer = setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
      return () => clearTimeout(focusTimer);
    }
  }, [isOpen]);

  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').replace(/\D/g, '');

    if (pasteData.length === 6) {
      const newOtp = pasteData.split('');
      setOtp(newOtp);
      // Focus last input
      inputRefs.current[5]?.focus();
    }
  };
  const handleVerifyOTP = async () => {
    const otpString = otp.join('');

    if (otpString.length !== 6) {
      toast({
        title: 'Invalid OTP',
        description: 'Please enter all 6 digits.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const response = await verifyOTPMutation.mutateAsync({
        email,
        otp: otpString,
      });

      toast({
        title: 'Email Verified!',
        description:
          response.message || 'Your email has been successfully verified.',
        variant: 'success',
      });

      // Pass the response data (which should include organizationId) to the parent
      onVerificationSuccess(response.userData);
      onClose();
    } catch (error: any) {
      handleError(error, {
        customMessage: 'Invalid OTP. Please check and try again.',
      });
    }
  };

  const handleResendOTP = async () => {
    try {
      const response = await resendOTPMutation.mutateAsync({ email });

      toast({
        title: 'OTP Sent',
        description: response.message || 'A new verification code has been sent to your email.',
        variant: 'success',
      });

      // Reset timer
      setTimeLeft(30);
      setCanResend(false);
    } catch (error: any) {
      handleError(error, {
        customMessage: 'Failed to resend OTP. Please try again.',
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="sm:max-w-md"
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center">
          <DialogTitle className="font-newsreader text-xl font-semibold tracking-tight tracking-tight text-foreground">
            Verify Your Email
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-2">
            We’ve sent a 6-digit code to your email. Enter it below to complete
            your signup.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* OTP Input */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3 text-center">
              Enter verification code
            </label>
            <div className="flex justify-center gap-0">
              {otp.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="w-12 h-12 text-center font-newsreader text-xl font-semibold tracking-tight tracking-tight border-2 focus:border-blue-500 focus:ring-blue-500"
                  disabled={verifyOTPMutation.isPending}
                />
              ))}
            </div>
          </div>

          {/* Resend Section */}
          <div className="flex items-center justify-center gap-1">
            <p className="text-sm text-muted-foreground">Not received the code?</p>

            {canResend ? (
              <Button
                variant="ghost"
                onClick={handleResendOTP}
                disabled={resendOTPMutation.isPending}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-0 h-auto font-medium"
              >
                {resendOTPMutation.isPending ? (
                  <span className="flex items-center">
                    <RefreshCwIcon className="w-4 h-4 mr-1 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  'Resend Code'
                )}
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Resend code in {timeLeft}s
              </p>
            )}
          </div>

          {/* Verify Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleVerifyOTP}
              disabled={
                verifyOTPMutation.isPending || otp.join('').length !== 6
              }
              className="w-1/2 bg-foreground-soft hover:bg-foreground text-primary-foreground disabled:opacity-50"
            >
              {verifyOTPMutation.isPending ? (
                <span className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Verifying...
                </span>
              ) : (
                <span className="flex items-center">
                  <CheckIcon className="w-4 h-4 mr-2" />
                  Verify Email
                </span>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
